import { CognitoUserPool, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import { config } from "./config";

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.clientId,
});

export const signUp = (email, password) => {
  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, [], null, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const confirmSignUp = (email, code) => {
  return new Promise((resolve, reject) => {
    const userData = { Username: email, Pool: userPool };
    const cognitoUser = new CognitoUser(userData);
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const resendConfirmationCode = (email) => {
  return new Promise((resolve, reject) => {
    const userData = { Username: email, Pool: userPool };
    const cognitoUser = new CognitoUser(userData);
    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const signIn = (email, password, newPassword = null) => {
  return new Promise((resolve, reject) => {
    const userData = { Username: email, Pool: userPool };
    const cognitoUser = new CognitoUser(userData);
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (result) => {
        resolve({
          token: result.getIdToken().getJwtToken(),
          email: email,
        });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // User needs to set a new password
        if (newPassword) {
          // If new password provided, complete the challenge
          delete userAttributes.email_verified;
          delete userAttributes.email;
          cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
            onSuccess: (result) => {
              resolve({
                token: result.getIdToken().getJwtToken(),
                email: email,
              });
            },
            onFailure: (err) => {
              reject(err);
            },
          });
        } else {
          // Return special response indicating password change needed
          resolve({
            challengeName: "NEW_PASSWORD_REQUIRED",
            cognitoUser: cognitoUser,
            userAttributes: userAttributes,
            email: email,
          });
        }
      },
    });
  });
};

export const signOut = () => {
  // Clear OAuth tokens from session storage
  sessionStorage.removeItem("idToken");
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");

  // Sign out from Cognito user pool
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    // First check if we have OAuth tokens in session storage
    const idToken = sessionStorage.getItem("idToken");
    if (idToken) {
      try {
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        // Check if token is expired
        if (payload.exp * 1000 > Date.now()) {
          resolve({
            token: idToken,
            email: payload.email,
          });
          return;
        } else {
          // Token expired, clear it
          sessionStorage.removeItem("idToken");
          sessionStorage.removeItem("accessToken");
          sessionStorage.removeItem("refreshToken");
        }
      } catch (err) {
        // Invalid token, clear it
        sessionStorage.removeItem("idToken");
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
      }
    }

    // Fall back to Cognito user pool
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const email = attributes.find((attr) => attr.Name === "email")?.Value;
        resolve({
          token: session.getIdToken().getJwtToken(),
          email: email,
        });
      });
    });
  });
};

export const completeNewPassword = (cognitoUser, newPassword, userAttributes) => {
  return new Promise((resolve, reject) => {
    delete userAttributes.email_verified;
    delete userAttributes.email;
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (result) => {
        resolve({
          token: result.getIdToken().getJwtToken(),
        });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

export const signInWithMidway = () => {
  const cognitoAuthUrl = `https://${config.oauthDomain}/oauth2/authorize`;
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    scope: "openid",
    redirect_uri: window.location.origin,
    identity_provider: config.identityProvider,
  });

  window.location.href = `${cognitoAuthUrl}?${params.toString()}`;
};

export const handleOAuthCallback = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");

  if (!code) {
    return null;
  }

  try {
    // Exchange code for tokens
    const tokenUrl = `https://${config.oauthDomain}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      code: code,
      redirect_uri: window.location.origin,
    });

    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    });

    if (!response.ok) {
      throw new Error("Token exchange failed");
    }

    const tokens = await response.json();

    // Decode ID token to get user info
    const idToken = tokens.id_token;
    const payload = JSON.parse(atob(idToken.split(".")[1]));

    // Store tokens in session storage
    sessionStorage.setItem("idToken", idToken);
    sessionStorage.setItem("accessToken", tokens.access_token);
    if (tokens.refresh_token) {
      sessionStorage.setItem("refreshToken", tokens.refresh_token);
    }

    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);

    return {
      token: idToken,
      email: payload.email,
    };
  } catch (err) {
    console.error("OAuth callback error:", err);
    throw err;
  }
};
